import { Injectable, inject } from '@angular/core';
import { catchError, firstValueFrom, map, Observable } from 'rxjs';
import { AuthService } from '@core/auth/auth.service';
import { AircraftService, OrganizationsService } from '@core/api/generated';
import type {
  DialCodeRead,
  OperatorAuthorizationCreate,
  OperatorAuthorizationRead,
  OperatorAuthorizationTypeRead,
  OrganizationAircraftRead,
  OrganizationCreate,
  OrganizationInfrastructureRead,
  OrganizationListItem,
  OrganizationRead,
  OrganizationUpdate,
  RoleCategoryRead,
} from '@core/api/generated';
import { ApiDataService } from '@core/api/data.service';
import { throwApiError } from '@core/error/api-error-mapper';

@Injectable({
  providedIn: 'root',
})
export class OrganizationApi {
  private readonly organizations = inject(OrganizationsService);
  private readonly aircraftService = inject(AircraftService);
  private readonly catalogApi = inject(ApiDataService);
  private readonly auth = inject(AuthService);

  fetchOrganizations(
    q?: string,
    limit?: number,
    offset?: number,
  ): Observable<{ items: OrganizationListItem[]; total: number }> {
    return this.organizations
      .listOrganizationsApiV1OrganizationsGet(q, undefined, undefined, limit, offset)
      .pipe(map((page) => ({ items: page.items, total: page.total })));
  }

  deleteOrganization(id: number): Observable<any> {
    return this.organizations.deleteOrganizationApiV1OrganizationsOrganizationIdDelete(id);
  }

  deactivateOrganization(id: number): Observable<OrganizationRead> {
    return this.organizations.deactivateOrganizationApiV1OrganizationsOrganizationIdDeactivatePost(
      id,
    );
  }

  reactivateOrganization(id: number): Observable<OrganizationRead> {
    return this.organizations.reactivateOrganizationApiV1OrganizationsOrganizationIdReactivatePost(
      id,
    );
  }

  submitOrganization(id: number): Observable<OrganizationRead> {
    return this.organizations.submitOrganizationApiV1OrganizationsOrganizationIdSubmitPost(id);
  }

  approveOrganization(id: number): Observable<OrganizationRead> {
    return this.organizations.approveOrganizationApiV1OrganizationsOrganizationIdApprovePost(id);
  }

  getOrganizationById(orgId: number, translations?: any): Observable<OrganizationRead> {
    return this.organizations
      .getOrganizationApiV1OrganizationsOrganizationIdGet(orgId)
      .pipe(catchError((error) => throwApiError(error, translations, 'FETCH_ORGANIZATION_ERROR')));
  }

  getInfrastructureLinks(organizationId: number): Observable<OrganizationInfrastructureRead[]> {
    return this.organizations
      .listOrganizationInfrastructuresApiV1OrganizationsOrganizationIdInfrastructuresGet(
        organizationId,
      )
      .pipe(map((page) => page.items));
  }

  getAircraftLinks(organizationId: number): Observable<OrganizationAircraftRead[]> {
    return this.organizations
      .listOrganizationAircraftsApiV1OrganizationsOrganizationIdAircraftsGet(organizationId)
      .pipe(map((page) => page.items));
  }

  update(id: number, payload: OrganizationUpdate): Observable<OrganizationRead> {
    return this.organizations.autosaveOrganizationApiV1OrganizationsOrganizationIdPatch(
      id,
      payload,
    );
  }

  create(payload: OrganizationCreate): Observable<OrganizationRead> {
    return this.organizations.createOrganizationApiV1OrganizationsPost(payload);
  }

  getRoles(organizationId: number): Observable<RoleCategoryRead[]> {
    return this.organizations.listRolesApiV1OrganizationsOrganizationIdRolesGet(organizationId);
  }

  listAuthorizations(organizationId: number): Observable<OperatorAuthorizationRead[]> {
    return this.organizations.listOperatorAuthorizationsApiV1OrganizationsOrganizationIdOperatorAuthorizationsGet(
      organizationId,
    );
  }

  addAuthorization(
    organizationId: number,
    body: OperatorAuthorizationCreate,
  ): Observable<OperatorAuthorizationRead> {
    return this.organizations.addOperatorAuthorizationApiV1OrganizationsOrganizationIdOperatorAuthorizationsPost(
      organizationId,
      body,
    );
  }

  removeAuthorization(organizationId: number, authorizationId: number): Observable<any> {
    return this.organizations.removeOperatorAuthorizationApiV1OrganizationsOrganizationIdOperatorAuthorizationsAuthorizationIdDelete(
      organizationId,
      authorizationId,
    );
  }

  async syncCategories(organizationId: number, desiredSubCategoryIds: number[]): Promise<void> {
    const currentSubCategories = await firstValueFrom(
      this.organizations.listSubCategoriesApiV1OrganizationsOrganizationIdSubCategoriesGet(
        organizationId,
      ),
    );

    const currentSubCategoryIds = new Set(
      currentSubCategories.map((link) => link.org_sub_category_id),
    );
    const desiredSubCategorySet = new Set(desiredSubCategoryIds);
    const toAdd = desiredSubCategoryIds.filter((id) => !currentSubCategoryIds.has(id));
    const toRemove = currentSubCategories.filter(
      (link) => !desiredSubCategorySet.has(link.org_sub_category_id),
    );

    await Promise.all([
      ...toAdd.map((id) =>
        firstValueFrom(
          this.organizations.addSubCategoryApiV1OrganizationsOrganizationIdSubCategoriesPost(
            organizationId,
            { org_sub_category_id: id },
          ),
        ),
      ),
      ...toRemove.map((link) =>
        firstValueFrom(
          this.organizations.removeSubCategoryApiV1OrganizationsOrganizationIdSubCategoriesOrgSubCategoryIdDelete(
            organizationId,
            link.org_sub_category_id,
          ),
        ),
      ),
    ]);
  }

  getOperatorAuthorizationTypes(): Observable<OperatorAuthorizationTypeRead[]> {
    return this.catalogApi.getOperatorAuthorizationTypes();
  }

  getDialCodes(): Observable<DialCodeRead[]> {
    return this.catalogApi.getDialCodes();
  }

  // desiredAircraftIds must be the organization's complete aircraft-link selection —
  // AircraftListCard's org edit-mode now builds `selectedRows` from the full,
  // unpaginated organization aircraft list (not just the current catalog page), so
  // any id missing here really has been deselected and is safe to unlink.
  async syncAircraft(organizationId: number, desiredAircraftIds: number[]): Promise<void> {
    const current = await firstValueFrom(this.getAircraftLinks(organizationId));
    const currentIds = new Set(current.map((item) => item.id));
    const desiredIds = new Set(desiredAircraftIds);
    const toAdd = desiredAircraftIds.filter((id) => !currentIds.has(id));
    const toRemove = current.filter((item) => !desiredIds.has(item.id));

    await Promise.all([
      ...toAdd.map(async (aircraftId) => {
        await firstValueFrom(
          this.aircraftService.addOrgLinkApiV1AircraftAircraftIdOrganizationsPost(aircraftId, {
            org_id: organizationId,
          }),
        );
        await firstValueFrom(
          this.aircraftService.transitionOrgLinkApiV1AircraftAircraftIdOrganizationsOrganizationIdActionPost(
            aircraftId,
            organizationId,
            'submit',
          ),
        );
        // Superadmins add aircraft links directly; everyone else's addition stays
        // pending (DRAFT) as an org-link petition for a superadmin to approve.
        if (this.auth.isSuperAdmin()) {
          await firstValueFrom(
            this.aircraftService.transitionOrgLinkApiV1AircraftAircraftIdOrganizationsOrganizationIdActionPost(
              aircraftId,
              organizationId,
              'approve',
            ),
          );
        }
      }),
      ...toRemove.map((item) =>
        firstValueFrom(
          this.aircraftService.removeOrgLinkApiV1AircraftAircraftIdOrganizationsOrganizationIdDelete(
            item.id,
            organizationId,
          ),
        ),
      ),
    ]);
  }
}
